/**
 * tutorHistoryAdapter — les séances passées du masteur (`GET tutors/live-occurrences`, rangées par le
 * réducteur dans `tutors.liveOccurrences`) → les lignes de « Mon historique de lives ».
 *
 * Port littéral des colonnes de `common/components/livesHistory/LivesHistory.js` :
 *  - le statut : « En attente » pour `pending_review`, ou une séance validée sans virement (pas de
 *    `transferred_at`, ou un montant de 0) ; `todo` passée → « Non fait », à venir → « À faire » ;
 *    tout le reste → « Validé » ;
 *  - le montant : `transfer_amount` en euros au centime (« 24,00 € ») s'il n'est pas nul, sinon « - » ;
 *  - sous la ligne, un compte-rendu par élève : son nom, sa classe, son e-mail, « 1er mois »
 *    (`is_student_subscribed_new`), « Rattrapage » (l'élève est dans `temporary_student_ids`), le score
 *    d'engagement s'il existe, la note, les étiquettes (« emoji libellé ») et le commentaire du masteur.
 *
 * Jamais importé par le DS.
 */
import moment from "moment"

import { eurosCents } from "../../../ui/formatters/moneyFormatter"
import { type UITutorHistoryOccurrence, type UITutorHistoryReport } from "../../../ui/types/tutorLives"
import { type ApiEngagementScores, toUITutorEngagement } from "../shared/tutorEngagementAdapter"
import { tagLabelOf } from "./tutorCloseOutAdapter"

type ApiTag = { emoji?: string | null; label?: string | null }

export type ApiTutorHistoryReport = {
    id?: string | number
    hash?: string | null
    is_student_subscribed_new?: boolean
    tutor_rating?: number | null
    tutor_tags?: ApiTag[] | null
    tutor_comment?: string | null
    statistics?: { scores?: ApiEngagementScores | null } | null
    student?: {
        id?: string | number
        display_name?: string | null
        email?: string | null
        avatar?: string | null
        profile?: { class?: string | null } | null
    } | null
}

export type ApiTutorHistoryOccurrence = {
    id?: string | number
    date?: string | null
    description?: string | null
    status?: string | null
    transferred_at?: string | null
    transfer_amount?: number | null
    temporary_student_ids?: Array<string | number> | null
    live_reports?: ApiTutorHistoryReport[] | null
}

/** Un compte-rendu de la ligne, plus ce que ses gestes lisent (pas rendu). */
export type TutorHistoryReportRow = UITutorHistoryReport & {
    // ── Action metadata ──
    /** La fiche de l'élève (`student-profil`). */
    studentId?: string | number
    /** Le rapport de la séance (`r?h=`). */
    hash?: string
}

export type TutorHistoryOccurrenceRow = UITutorHistoryOccurrence & { reports: TutorHistoryReportRow[] }

function statusOf(raw: ApiTutorHistoryOccurrence, now: moment.Moment): UITutorHistoryOccurrence["status"] {
    const pending =
        raw.status === "pending_review" ||
        (raw.status === "validated" && (!raw.transferred_at || raw.transfer_amount === 0))
    if (pending) return "pending"
    if (raw.status === "todo") return now.isAfter(raw.date ?? undefined) ? "missed" : "todo"
    return "validated"
}

function toReport(raw: ApiTutorHistoryReport, temporaryIds: string[]): TutorHistoryReportRow | null {
    const student = raw.student
    if (raw.id == null || student?.id == null) return null
    const className = student.profile?.class ?? undefined
    const engagement = toUITutorEngagement(raw.statistics?.scores)
    const comment = raw.tutor_comment ?? undefined
    return {
        id: raw.id,
        student: {
            id: student.id,
            name: student.display_name ?? "",
            ...(student.avatar ? { uri: student.avatar } : {}),
            ...(className ? { className } : {}),
            ...(student.email ? { email: student.email } : {}),
        },
        ...(raw.is_student_subscribed_new ? { firstMonth: true } : {}),
        ...(temporaryIds.includes(String(student.id)) ? { recovery: true } : {}),
        ...(engagement != null ? { engagement } : {}),
        rating: raw.tutor_rating ?? 0,
        tags: (raw.tutor_tags ?? []).filter((tag) => !!tag?.label).map(tagLabelOf),
        ...(comment ? { comment } : {}),
        studentId: student.id,
        ...(raw.hash ? { hash: raw.hash } : {}),
    }
}

export function toTutorHistoryOccurrences(
    raws: ReadonlyArray<ApiTutorHistoryOccurrence | null | undefined> | null | undefined,
    now: moment.Moment | Date | number = moment()
): TutorHistoryOccurrenceRow[] {
    const at = moment(now)
    return (raws ?? [])
        .map((raw): TutorHistoryOccurrenceRow | null => {
            if (raw?.id == null || !raw.date) return null
            const temporaryIds = (raw.temporary_student_ids ?? []).map(String)
            return {
                id: raw.id,
                date: moment(raw.date).format("YYYY-MM-DDTHH:mm:ss"),
                ...(raw.description ? { description: raw.description } : {}),
                status: statusOf(raw, at),
                // Le serveur sert des euros : l'arrondi au centime évite qu'un flottant ne déborde.
                ...(raw.transfer_amount
                    ? { amountLabel: eurosCents(Math.round(Number(raw.transfer_amount) * 100)) }
                    : {}),
                ...(raw.transferred_at ? { transferredAt: raw.transferred_at } : {}),
                reports: (raw.live_reports ?? [])
                    .map((report) => (report == null ? null : toReport(report, temporaryIds)))
                    .filter((report): report is TutorHistoryReportRow => report != null),
            }
        })
        .filter((row): row is TutorHistoryOccurrenceRow => row != null)
}
